import { useCallback, useEffect, useRef, useState } from "react";
import type React from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

const MESSAGE = "Kaydedilmemiş değişiklikler var. Sayfadan çıkmak istediğinize emin misiniz?";

// Kaydedilmemiş değişiklik koruması.
//
// Uygulama <BrowserRouter> kullanıyor (data router değil); react-router'ın
// useBlocker'ı yalnızca createBrowserRouter/RouterProvider altında çalışır.
// Bu yüzden:
//  - sekme kapatma / yenileme / dış adrese gitme → beforeunload
//  - uygulama içi <a>/<Link> tıklamaları (kenar menü vb.) → capture fazında
//    yakalanıp onay diyaloğu gösterilir
//  - formun kendi İptal/Geri butonları → dönen confirmNavigate()
// Tarayıcının geri/ileri tuşu (popstate) BrowserRouter ile engellenemez.
export function useUnsavedChanges(dirty: boolean): {
  confirmNavigate: (to: string) => void;
  dialog: React.ReactNode;
} {
  const navigate = useNavigate();
  const [pendingTo, setPendingTo] = useState<string | null>(null);
  const dirtyRef = useRef(dirty);

  useEffect(() => {
    dirtyRef.current = dirty;
  }, [dirty]);

  useEffect(() => {
    if (!dirty) return;

    function onBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault();
      // Eski tarayıcılar için; metin modern tarayıcılarda gösterilmez.
      e.returnValue = MESSAGE;
      return MESSAGE;
    }

    function onClickCapture(e: MouseEvent) {
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (!(e.target instanceof Element)) return;
      const anchor = e.target.closest("a[href]");
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (anchor.target && anchor.target !== "_self") return;
      if (anchor.hasAttribute("download")) return;
      // Editör içeriğindeki linkler gezinme değildir (TipTap'te tıklama yalnızca imleç koyar).
      if (anchor.closest("[contenteditable='true'], .ProseMirror")) return;

      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      const to = `${url.pathname}${url.search}${url.hash}`;
      // Aynı sayfa içi #bağlantı veya aynı adres: sayfadan çıkılmıyor.
      if (`${url.pathname}${url.search}` === `${window.location.pathname}${window.location.search}`)
        return;

      // Link'in kendi onClick'i (root'ta dinlenir) çalışmasın; onaydan sonra biz gideriz.
      e.preventDefault();
      e.stopPropagation();
      setPendingTo(to);
    }

    window.addEventListener("beforeunload", onBeforeUnload);
    document.addEventListener("click", onClickCapture, true);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("click", onClickCapture, true);
    };
  }, [dirty]);

  const confirmNavigate = useCallback(
    (to: string) => {
      if (dirtyRef.current) setPendingTo(to);
      else navigate(to);
    },
    [navigate],
  );

  const dialog = (
    <AlertDialog
      open={pendingTo !== null}
      onOpenChange={(open) => {
        if (!open) setPendingTo(null);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Kaydedilmemiş değişiklikler</AlertDialogTitle>
          <AlertDialogDescription>{MESSAGE}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => setPendingTo(null)}>
            Sayfada Kal
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              const to = pendingTo;
              setPendingTo(null);
              if (to) navigate(to);
            }}
          >
            Çık
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { confirmNavigate, dialog };
}
